import React, { useState } from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { Heading } from '~/components/core';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import AuditLogTable from '~/components/SystemConfiguration/ConfigurationManagement/AuditLog/AuditLogTable';
import TextFieldFormik from '~/components/TextFieldFormik';
import type { UserModel } from '~/components/types/user-types';
import type { AuditedConfigurationChangeList } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

interface CreateChangeRequestDialogProps {
  onClose: () => void;
  organization: Record<string, unknown>;
  onSuccess: () => void;
  configurationChanges?: AuditedConfigurationChangeList;
  users?: UserModel[];
  isLoadingChanges: boolean;
  isErrorChanges: boolean;
  isLoadingUsers: boolean;
  isErrorUsers: boolean;
}

interface CreateChangeRequestForm {
  changeset_description: string;
}

const CreateChangeRequestDialog: React.FC<CreateChangeRequestDialogProps> = ({
  onClose,
  organization,
  onSuccess,
  configurationChanges = [],
  users,
  isLoadingChanges,
  isErrorChanges,
  isLoadingUsers,
  isErrorUsers,
}) => {
  const [selectedChangeIds, setSelectedChangeIds] = useState<string[]>([]);

  const initialValues: CreateChangeRequestForm = {
    changeset_description: '',
  };

  const validationSchema = Yup.object().shape({
    changeset_description: Yup.string().required('Required'),
  });

  const handleSubmit = async (values: CreateChangeRequestForm) => {
    try {
      // Validate that at least one change is selected
      if (selectedChangeIds.length === 0) {
        throw new Error('Please select at least one configuration change');
      }

      const requestData = {
        changeset_description: values.changeset_description,
        selected_change_ids: selectedChangeIds,
      };

      await axios.post(`/sysconfig/api/v1/organizations/${organization.id}/change_requests`, requestData);

      onSuccess();
    } catch (error) {
      reportAxiosError(error);
      throw error;
    }
  };

  const displayLoading =
    isLoadingUsers || isErrorUsers || !users || isLoadingChanges || isErrorChanges || !configurationChanges;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await handleSubmit(values);
        } catch (error) {
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <CardDialog
          isDialog
          title="Create Change Request"
          maxWidth="xl"
          onClose={onClose}
          preventClose={isSubmitting}
          footerActions={
            <DialogFooterActions
              onClickPrimary={handleSubmit}
              onClickSecondary={onClose}
              primaryLabel="Create Change Request"
              showPrimary
              secondaryLabel="Cancel"
              ignorePermissionsSecondary
            />
          }
        >
          <div className="space-y-24">
            {/* Form Fields */}
            <div className="space-y-16">
              <TextFieldFormik
                id="changeset_description"
                label="Description"
                placeholder="Enter a description for this change request"
                fullWidth
                multiline
                rows={3}
              />
            </div>

            {/* Configuration Changes Table */}
            <div>
              <Heading className="mb-16">Select Configuration Changes</Heading>
              <div className="rounded-md border border-slate-200 p-16">
                {displayLoading ? (
                  <SkeletonTable rowsCount={5} columnsCount={8} isError={isErrorChanges || isErrorUsers} />
                ) : (
                  <>
                    <div className="mb-16 text-sm text-slate-600">
                      Select the configuration changes to include in this change request.
                      {selectedChangeIds.length > 0 && (
                        <span className="ml-8 font-medium text-blue-600">
                          ({selectedChangeIds.length} change{selectedChangeIds.length !== 1 ? 's' : ''} selected)
                        </span>
                      )}
                    </div>
                    <AuditLogTable
                      rows={configurationChanges}
                      users={users || []}
                      selectedChangeIds={selectedChangeIds}
                      onSelectedChangeIdsChange={setSelectedChangeIds}
                      withSelectChanges={true}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default CreateChangeRequestDialog;
